// ---- his field ----
// The engine owns who is standing where and the collisions that keep them out
// of each other (core/field.ts). This says how big his field is, what shape he
// is in it, and the two things that are his alone: how an animal arrives, and
// what every animal does each frame.
import {admit,bodies,elder,elderBody,fieldSpot,grow,inFrame,separate,setField,FIELD} from '../../core/field';
import {makeAnimal,pause} from './animals';
import {pondSpot} from '../../core/water';
import {rnd} from '../../core/util';
import type {AnimalSpec} from './data';
import type {Animal,Brain} from './types';

// His names for the engine's things. `animals` IS `bodies` — the same array,
// not a copy: brains.ts reads it to find a neighbour to stalk, and round.ts
// counts it for the badge, and a filtered copy would quietly break both.
export const animals=bodies as Animal[];
export const jiddo=elder, jiddoBody=elderBody;
export {fieldSpot,FIELD};

jiddo.position.set(-3.4,0,1.4);
setField({
  bounds:{x:8.5,zFar:-8.5,zNear:2.6},
  capsule:[[0.55,0.55],[1.45,0.55],[2.05,0.4]],   // up his body
  spacing:1.5, cap:8,
  // A bumped animal stops and thinks again rather than ploughing through; a
  // brain may care who touched it, which is what `bumped` is for. Only a
  // walker pushed BACK — one that walked into someone — stops: a push from the
  // side or from behind is the shuffle sliding it past, and it walks on.
  // Stopping on every touch was how two animals that came to rest touching
  // stood for minutes, each one's first step cancelled by the other.
  onBump:(b,t,nx,nz)=>{const e=b as Animal;
    if(e.st==='hop')pause(e,t,500,1400);
    else if(e.st==='walk'||e.st==='swim'){
      const dx=e.tx-e.position.x, dz=e.tz-e.position.z, d=Math.hypot(dx,dz)||1;
      if((!nx&&!nz)||(nx*dx+nz*dz)/d<-0.2)pause(e,t,500,1400);   // no direction given (shoved off the water): a full stop, as before
    }},
});

// a new animal, somewhere clear and in the frame, facing you
export function arrive(spec:AnimalSpec,brain:Brain,t:number){
  const a=makeAnimal(spec,brain), s=spec.water?pondSpot():fieldSpot(null,a.pad,inFrame);   // a fish arrives in the pond
  a.position.set(s.x,0,s.z); a.rotation.y=rnd(-0.7,0.7);   // arrives facing you
  pause(a,t,600,1900);
  return admit(a,t) as Animal;
}
// Every brain's frame, then the collisions. A brain that throws is caught here,
// because three.js asks for the next frame only after this one returns:
// uncaught, one animal's bug ended the loop, and the game stood still for good
// while the taps went on sounding (a hushed sheep did exactly that, once). So
// the throw costs its animal one decision — it is stood back in a pause, to
// think again — and is logged once per animal; everyone else, and the frame,
// carry on.
const complained=new WeakSet<Animal>();
export function updateField(t:number,dt:number){
  for(const a of animals){
    try{a.brain(a,t,dt);}
    catch(err){
      if(!complained.has(a)){complained.add(a);console.error(a.w+': its brain threw, in state '+a.st,err);}
      pause(a,t,1500,4000);
    }
    grow(a,t);
  }
  separate(t);
}
